import { useEffect, useState } from 'react';

import { Icon } from '@travelnest/hui';

import { PillButton } from '@/components/layout/SiteHeader';
import { useI18n } from '@/locales';

interface ShareButtonProps {
	title: string;
	text: string;
	url: string;
}

/**
 * The header's Share pill: the native share sheet where the browser has one,
 * otherwise copies the link and says so for a moment.
 */
export const ShareButton = ({ title, text, url }: ShareButtonProps) => {
	const t = useI18n();
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (!copied) return;
		const timer = window.setTimeout(() => setCopied(false), 2000);
		return () => window.clearTimeout(timer);
	}, [copied]);

	const share = async () => {
		const data = { title, text, url };
		if (
			typeof navigator.share === 'function' &&
			(!navigator.canShare || navigator.canShare(data))
		) {
			try {
				await navigator.share(data);
				return;
			} catch {
				// The guest dismissed the share sheet; fall through to copying.
			}
		}
		await navigator.clipboard?.writeText(url);
		setCopied(true);
	};

	return (
		<PillButton type="button" onClick={() => void share()}>
			<Icon name={copied ? 'Checkmark' : 'Share'} size={24} />
			<span aria-live="polite">
				{copied
					? t('property.share.copied')
					: t('property.share.label')}
			</span>
		</PillButton>
	);
};
